// McpServers.tsx — manage MCP server entries (browse catalog, install,
// configure env vars, enable/disable).
import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Row, Col, Badge, ListGroup, Modal, Spinner, Alert } from 'react-bootstrap';
import { BsPlus, BsTrash, BsSearch, BsSave } from 'react-icons/bs';
import { ui } from 'common';
import { mcpApi } from '../api';
import type { MCPServer } from '../types';
import Text from './ui/Text';
import JsonArgsField from './mcp/JsonArgsField';

const McpServers: React.FC = () => {
  const [servers, setServers] = useState<MCPServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  // Failures render as an outlined alert on the page (C-MSG-1), and
  // destructive actions confirm through the shared Modal (C-MSG-3) —
  // neither goes through the browser's native dialogs.
  const [pageError, setPageError] = useState('');
  const [confirm, confirmDialog] = ui.useConfirm();
  // False while the visible args textarea holds text that is not a JSON
  // array of strings; Discover and Save would otherwise use a stale value.
  const [argsValid, setArgsValid] = useState(true);

  const [newServer, setNewServer] = useState<Partial<MCPServer>>({
    type: 'oci',
    config: { image: '', runtime_args: [] },
    tools: []
  });

  const loadServers = async () => {
    setLoading(true);
    try {
      const data = await mcpApi.list();
      setServers(data);
    } catch (err) {
      console.error('Failed to load MCP servers:', err);
      // Without this the page reads as "no servers configured".
      setPageError(`Loading MCP servers failed: ${err}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadServers();
  }, []);

  const handleDiscover = async () => {
    if (!newServer.config?.image) return;
    if (!argsValid) {
      setPageError('Fix the runtime args JSON before discovering.');
      return;
    }
    setPageError('');
    setDiscovering(true);
    try {
      const result = await mcpApi.discover(newServer.config.image, newServer.config.runtime_args);
      setNewServer(prev => ({
        ...prev,
        tools: result.tools
      }));
    } catch (err) {
      setPageError(`Discovery failed: ${err}`);
    } finally {
      setDiscovering(false);
    }
  };

  const handleSave = async () => {
    if (!newServer.id || !newServer.name) {
      setPageError('ID and Name are required.');
      return;
    }
    if (!argsValid) {
      setPageError('Fix the args JSON before saving.');
      return;
    }
    try {
      await mcpApi.save(newServer as MCPServer);
      setShowAddModal(false);
      loadServers();
    } catch (err) {
      setPageError(`Save failed: ${err}`);
    }
  };

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      title: 'Delete MCP Server',
      body: 'Delete this server? Sessions that had it enabled lose its tools.',
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    try {
      await mcpApi.delete(id);
      loadServers();
    } catch (err) {
      setPageError('Delete failed.');
    }
  };

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="text-info mb-0">MCP SERVERS</h2>
        <Button variant="info" onClick={() => {
          setNewServer({ id: '', name: '', type: 'oci', config: { image: '', runtime_args: [] }, tools: [] });
          setShowAddModal(true);
        }}>
          <BsPlus size={20} /> ADD SERVER
        </Button>
      </div>

      {pageError && (
        <Alert variant="danger" dismissible onClose={() => setPageError('')}>
          {pageError}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="info" />
        </div>
      ) : (
        <Row className="g-4">
          {servers.map(server => (
            <Col key={server.id} md={6} xl={4}>
              {/* C-CARD-4: no outline. The header keeps Bootswatch's cap fill
                  rather than a transparent band with a rule under it — that
                  fill is what separates the slots now. */}
              <Card className="h-100 bg-dark">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center gap-2">
                    <Badge bg={server.type === 'oci' ? 'primary' : 'success'}>
                      {server.type.toUpperCase()}
                    </Badge>
                    <span className="fw-bold">{server.name}</span>
                  </div>
                  <Button variant="outline-danger" size="sm" onClick={() => handleDelete(server.id)}>
                    <BsTrash />
                  </Button>
                </Card.Header>
                <Card.Body>
                  <div className="mb-3 small text-muted">
                    ID: <code className="text-info">{server.id}</code><br />
                    {server.type === 'oci' ? (
                      <>Image: <code className="text-warning">{server.config.image}</code></>
                    ) : (
                      <>Command: <code className="text-warning">{server.config.command}</code></>
                    )}
                  </div>
                  <div className="fw-bold small mb-2 text-uppercase text-muted">Tools ({server.tools?.length || 0})</div>
                  <div className="d-flex flex-wrap gap-1">
                    {server.tools?.slice(0, 10).map(t => (
                      <Badge key={t.name} bg="dark" className="border border-secondary text-info">
                        {t.name}
                      </Badge>
                    ))}
                    {(server.tools?.length || 0) > 10 && <span className="text-muted small">...</span>}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg" centered contentClassName="bg-dark text-light border-secondary">
        <Modal.Header closeButton closeVariant="white" className="border-secondary">
          <Modal.Title>Add MCP Server</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="small text-muted text-uppercase fw-bold">Server ID</Form.Label>
                  <Text value={newServer.id} onChange={e => setNewServer({ ...newServer, id: e.target.value })} placeholder="e.g. my-oci-tool" />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="small text-muted text-uppercase fw-bold">Display Name</Form.Label>
                  <Text value={newServer.name} onChange={e => setNewServer({ ...newServer, name: e.target.value })} placeholder="e.g. My OCI Tool" />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label className="small text-muted text-uppercase fw-bold">Type</Form.Label>
              <div className="d-flex gap-3">
                <Form.Check type="radio" label="OCI Container" name="type" checked={newServer.type === 'oci'} onChange={() => setNewServer({ ...newServer, type: 'oci' })} />
                <Form.Check type="radio" label="Stdio (Local)" name="type" checked={newServer.type === 'stdio'} onChange={() => setNewServer({ ...newServer, type: 'stdio' })} />
              </div>
            </Form.Group>

            {newServer.type === 'oci' ? (
              <>
                <Form.Group className="mb-3">
                  <Form.Label className="small text-muted text-uppercase fw-bold">Container Image</Form.Label>
                  <div className="d-flex gap-2">
                    <Text value={newServer.config?.image || ''} onChange={e => setNewServer({ ...newServer, config: { ...newServer.config!, image: e.target.value } })} placeholder="e.g. alpine:latest" />
                    <Button variant="outline-info" onClick={handleDiscover} disabled={discovering || !newServer.config?.image || !argsValid}>
                      {discovering ? <Spinner size="sm" animation="border" /> : <BsSearch />} Discover
                    </Button>
                  </div>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="small text-muted text-uppercase fw-bold">Runtime Args (JSON Array)</Form.Label>
                  <JsonArgsField
                    label="Runtime args"
                    value={newServer.config?.runtime_args}
                    onValidity={setArgsValid}
                    onChange={args => setNewServer(prev => ({ ...prev, config: { ...prev.config!, runtime_args: args } }))}
                  />
                  <Form.Text className="text-muted small">e.g. ["--network=host", "-v", "/tmp:/tmp"]</Form.Text>
                </Form.Group>
              </>
            ) : (
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label className="small text-muted text-uppercase fw-bold">Command</Form.Label>
                    <Text value={newServer.config?.command || ''} onChange={e => setNewServer({ ...newServer, config: { ...newServer.config!, command: e.target.value } })} placeholder="python3" />
                  </Form.Group>
                </Col>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label className="small text-muted text-uppercase fw-bold">Args (JSON Array)</Form.Label>
                    <JsonArgsField
                      label="Args"
                      value={newServer.config?.args}
                      onValidity={setArgsValid}
                      onChange={args => setNewServer(prev => ({ ...prev, config: { ...prev.config!, args } }))}
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}

            {newServer.tools && newServer.tools.length > 0 && (
              <div className="mt-4">
                <div className="small text-muted text-uppercase fw-bold mb-2">Discovered Tools</div>
                <ListGroup variant="flush" className="border-secondary rounded overflow-hidden">
                  {newServer.tools.map(tool => (
                    <ListGroup.Item key={tool.name} className="bg-dark text-light border-secondary">
                      <div className="d-flex justify-content-between">
                        <span className="text-info fw-bold">{tool.name}</span>
                        <div className="d-flex gap-1">
                          {Object.keys(tool.arguments || {}).map(arg => (
                            <Badge key={arg} bg="secondary" className="smaller">--{arg}</Badge>
                          ))}
                        </div>
                      </div>
                      <div className="smaller text-muted">{tool.description}</div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-secondary">
          <Button variant="outline-secondary" onClick={() => setShowAddModal(false)}>Cancel</Button>
          <Button variant="info" onClick={handleSave}>
            <BsSave className="me-2" /> Save Server
          </Button>
        </Modal.Footer>
      </Modal>

      {confirmDialog}
    </div>
  );
};

export default McpServers;
